import { useMemo, useState } from 'react';
import { Ban, Lock, Trash2 } from 'lucide-react';
import {
  agoraISO,
  fmtBrl,
  inicioFimSemanaISO,
  metodoLabel,
  statusLabel,
  uid,
} from '../../utils/barbeariaLogica';
import type {
  BarbeariaEstado,
  MetodoPagamento,
  StatusAgendamento,
} from '../../utils/barbeariaTipos';

interface Props {
  estado: BarbeariaEstado;
  patch: (fn: (prev: BarbeariaEstado) => BarbeariaEstado) => void;
}

type ModoAgenda = 'dia' | 'semana';
type PresetBloqueio = 'dia' | 'semana' | 'mes' | 'ano' | 'custom';

function isoDeDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function somarDiasISO(iso: string, dias: number): string {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + dias);
  return isoDeDate(d);
}

function fimPorPreset(ini: string, preset: PresetBloqueio): string {
  const d = parseISODate(ini);
  if (preset === 'dia') return ini;
  if (preset === 'semana') {
    d.setDate(d.getDate() + 6);
    return isoDeDate(d);
  }
  if (preset === 'mes') {
    d.setMonth(d.getMonth() + 1);
    d.setDate(d.getDate() - 1);
    return isoDeDate(d);
  }
  if (preset === 'ano') {
    d.setFullYear(d.getFullYear() + 1);
    d.setDate(d.getDate() - 1);
    return isoDeDate(d);
  }
  return ini;
}

function listarDatasEntre(iniISO: string, fimISO: string): string[] {
  const out: string[] = [];
  let cur = parseISODate(iniISO);
  const fim = parseISODate(fimISO);
  if (fim < cur) return [iniISO];
  // limite de segurança (~366 dias)
  for (let i = 0; i < 370 && cur <= fim; i++) {
    out.push(isoDeDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function BarbeariaAdmin({ estado, patch }: Props) {
  const [modo, setModo] = useState<ModoAgenda>('dia');
  const [dataRef, setDataRef] = useState(() => {
    const d = new Date();
    return isoDeDate(d);
  });
  const [bloqueioForm, setBloqueioForm] = useState({
    barbeiroId: '',
    horaInicio: '12:00',
    horaFim: '13:00',
    motivo: 'Almoço',
    dataInicio: '',
    dataFim: '',
    preset: 'dia' as PresetBloqueio,
  });
  const [msgBloqueio, setMsgBloqueio] = useState('');

  const dataInicioBloq = bloqueioForm.dataInicio || dataRef;
  const dataFimBloq =
    bloqueioForm.preset === 'custom'
      ? bloqueioForm.dataFim || dataInicioBloq
      : fimPorPreset(dataInicioBloq, bloqueioForm.preset);

  const semana = useMemo(() => {
    const [y, m, d] = dataRef.split('-').map(Number);
    return inicioFimSemanaISO(new Date(y, m - 1, d));
  }, [dataRef]);

  const lista = useMemo(() => {
    return estado.agendamentos
      .filter((a) => {
        if (modo === 'dia') return a.dataISO === dataRef;
        return a.dataISO >= semana.ini && a.dataISO <= semana.fim;
      })
      .sort((a, b) =>
        a.dataISO === b.dataISO
          ? a.horaInicio.localeCompare(b.horaInicio)
          : a.dataISO.localeCompare(b.dataISO)
      );
  }, [estado.agendamentos, dataRef, modo, semana.ini, semana.fim]);

  const setStatus = (id: string, status: StatusAgendamento) => {
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      agendamentos: prev.agendamentos.map((a) =>
        a.id === id ? { ...a, status, atualizadoEm: agora } : a
      ),
      atualizadoEm: agora,
    }));
  };

  const setPagamento = (id: string, metodoPagamento: MetodoPagamento) => {
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      agendamentos: prev.agendamentos.map((a) =>
        a.id === id ? { ...a, metodoPagamento, atualizadoEm: agora } : a
      ),
      atualizadoEm: agora,
    }));
  };

  const salvarNotasCliente = (clienteId: string, notas: string) => {
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      clientes: prev.clientes.map((c) =>
        c.id === clienteId ? { ...c, notas, atualizadoEm: agora } : c
      ),
      atualizadoEm: agora,
    }));
  };

  const apagarAgendamento = (id: string, quando: string) => {
    if (
      !window.confirm(
        `Apagar este horário (${quando})?\nO horário volta a ficar livre para novos clientes.`
      )
    ) {
      return;
    }
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      agendamentos: prev.agendamentos.filter((a) => a.id !== id),
      remocoesAgendamento: {
        ...(prev.remocoesAgendamento || {}),
        [id]: agora,
      },
      atualizadoEm: agora,
    }));
  };

  const adicionarBloqueio = () => {
    setMsgBloqueio('');
    const ini = dataInicioBloq;
    const fim = dataFimBloq;
    if (parseISODate(fim) < parseISODate(ini)) {
      setMsgBloqueio('A data final não pode ser antes da inicial.');
      return;
    }
    if (bloqueioForm.horaFim <= bloqueioForm.horaInicio) {
      setMsgBloqueio('Horário final deve ser depois do início.');
      return;
    }
    const dias = listarDatasEntre(ini, fim);
    const agora = agoraISO();
    const motivo = bloqueioForm.motivo.trim() || 'Bloqueado';
    patch((prev) => ({
      ...prev,
      bloqueios: [
        ...prev.bloqueios,
        ...dias.map((dataISO) => ({
          id: uid('blk'),
          barbeiroId: bloqueioForm.barbeiroId || null,
          dataISO,
          horaInicio: bloqueioForm.horaInicio,
          horaFim: bloqueioForm.horaFim,
          motivo:
            dias.length > 1 ? `${motivo} (${ini}→${fim})` : motivo,
        })),
      ],
      atualizadoEm: agora,
    }));
    setMsgBloqueio(
      dias.length === 1
        ? `Bloqueado em ${ini}.`
        : `Bloqueado em ${dias.length} dias (${ini} → ${fim}).`
    );
  };

  const removerBloqueio = (id: string) => {
    patch((prev) => ({
      ...prev,
      bloqueios: prev.bloqueios.filter((b) => b.id !== id),
      atualizadoEm: agoraISO(),
    }));
  };

  const setComissao = (barbeiroId: string, comissaoPct: number) => {
    patch((prev) => ({
      ...prev,
      barbeiros: prev.barbeiros.map((b) =>
        b.id === barbeiroId
          ? { ...b, comissaoPct: Math.min(100, Math.max(0, comissaoPct)) }
          : b
      ),
      atualizadoEm: agoraISO(),
    }));
  };

  const bloqueiosDoDia = estado.bloqueios.filter((b) => b.dataISO === dataRef);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 bb-card">
        <label className="text-xs font-medium text-neutral-600">
          Data de referência
          <input
            type="date"
            className="mt-1 block rounded-lg border px-3 py-2 text-sm"
            value={dataRef}
            onChange={(e) => setDataRef(e.target.value)}
          />
        </label>
        <div className="flex gap-1">
          {(['dia', 'semana'] as ModoAgenda[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setModo(m)}
              className={`rounded-lg border-2 px-3 py-2 text-xs font-bold uppercase shadow-sm ${
                modo === m
                  ? 'border-neutral-900 bg-neutral-900 text-white'
                  : 'border-neutral-500 bg-white text-neutral-800 hover:bg-amber-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
        {modo === 'semana' && (
          <p className="text-xs text-neutral-500">
            Semana {semana.ini} → {semana.fim}
          </p>
        )}
      </div>

      <section className="overflow-x-auto bb-card p-0" data-tour="adm-pedidos">
        <table className="w-full min-w-[800px] border-collapse text-sm">
          <thead className="bg-neutral-100 text-left text-xs uppercase text-neutral-600">
            <tr>
              <th className="border px-2 py-2">Quando</th>
              <th className="border px-2 py-2">Cliente</th>
              <th className="border px-2 py-2">Serviço</th>
              <th className="border px-2 py-2">Barbeiro</th>
              <th className="border px-2 py-2">Status</th>
              <th className="border px-2 py-2">Pagamento</th>
              <th className="border px-2 py-2">Notas do cliente</th>
              <th className="border px-2 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-neutral-500">
                  Nenhum agendamento neste período.
                </td>
              </tr>
            ) : (
              lista.map((a) => {
                const cli = estado.clientes.find((c) => c.id === a.clienteId);
                const srv = estado.servicos.find((s) => s.id === a.servicoId);
                const brb = estado.barbeiros.find((b) => b.id === a.barbeiroId);
                return (
                  <tr key={a.id} className="hover:bg-amber-50/40">
                    <td className="border px-2 py-2 font-mono text-xs">
                      {a.dataISO}
                      <br />
                      {a.horaInicio}
                    </td>
                    <td className="border px-2 py-2">
                      <div className="font-semibold">{cli?.nome || '—'}</div>
                      <div className="text-[11px] text-neutral-500">{cli?.telefone}</div>
                    </td>
                    <td className="border px-2 py-2">
                      {srv?.nome || '—'}
                      <div className="text-[11px] text-neutral-500">
                        {fmtBrl(a.valorCobrado ?? srv?.preco ?? 0)}
                      </div>
                    </td>
                    <td className="border px-2 py-2">{brb?.nome || '—'}</td>
                    <td className="border px-2 py-2">
                      <select
                        className="w-full rounded border px-1 py-1 text-xs"
                        value={a.status}
                        onChange={(e) => setStatus(a.id, e.target.value as StatusAgendamento)}
                      >
                        {(
                          ['confirmado', 'em_andamento', 'concluido', 'cancelado'] as StatusAgendamento[]
                        ).map((s) => (
                          <option key={s} value={s}>
                            {statusLabel(s)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="border px-2 py-2">
                      <select
                        className="w-full rounded border px-1 py-1 text-xs"
                        value={a.metodoPagamento || ''}
                        disabled={a.status !== 'concluido'}
                        onChange={(e) =>
                          setPagamento(a.id, e.target.value as MetodoPagamento)
                        }
                      >
                        <option value="">—</option>
                        {(['pix', 'dinheiro', 'credito', 'debito'] as MetodoPagamento[]).map(
                          (m) => (
                            <option key={m} value={m}>
                              {metodoLabel(m)}
                            </option>
                          )
                        )}
                      </select>
                    </td>
                    <td className="border px-2 py-2">
                      <textarea
                        className="w-full min-w-[10rem] rounded border px-1.5 py-1 text-xs"
                        rows={2}
                        defaultValue={cli?.notas || ''}
                        key={`${cli?.id}-${cli?.atualizadoEm}`}
                        placeholder="Preferência de corte, produtos…"
                        onBlur={(e) => {
                          if (cli) salvarNotasCliente(cli.id, e.target.value);
                        }}
                      />
                    </td>
                    <td className="border px-2 py-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg border-2 border-red-700 bg-red-50 px-2 py-1.5 text-[11px] font-bold text-red-800 hover:bg-red-100"
                        title="Apagar e liberar horário"
                        onClick={() => apagarAgendamento(a.id, `${a.dataISO} ${a.horaInicio}`)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Apagar
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="bb-card" data-tour="adm-bloqueio">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase text-neutral-800">
            <Lock className="h-4 w-4 text-amber-700" /> Bloquear horário
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <label className="col-span-2 text-xs">
              Barbeiro (vazio = todos)
              <select
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                value={bloqueioForm.barbeiroId}
                onChange={(e) =>
                  setBloqueioForm((f) => ({ ...f, barbeiroId: e.target.value }))
                }
              >
                <option value="">Todos</option>
                {estado.barbeiros.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nome}
                  </option>
                ))}
              </select>
            </label>

            <label className="col-span-2 text-xs font-medium text-neutral-700">
              Período do bloqueio
              <div className="mt-1 flex flex-wrap gap-1">
                {(
                  [
                    ['dia', '1 dia'],
                    ['semana', '1 semana'],
                    ['mes', '1 mês'],
                    ['ano', '1 ano'],
                    ['custom', 'Escolher datas'],
                  ] as Array<[PresetBloqueio, string]>
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      setBloqueioForm((f) => ({
                        ...f,
                        preset: id,
                        dataInicio: f.dataInicio || dataRef,
                        dataFim:
                          id === 'custom'
                            ? f.dataFim || somarDiasISO(f.dataInicio || dataRef, 6)
                            : fimPorPreset(f.dataInicio || dataRef, id),
                      }))
                    }
                    className={`rounded-lg border-2 px-2 py-1 text-[11px] font-bold ${
                      bloqueioForm.preset === id
                        ? 'border-amber-900 bg-amber-600 text-white'
                        : 'border-neutral-400 bg-white text-neutral-800 hover:bg-amber-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </label>

            <label className="text-xs">
              Data início
              <input
                type="date"
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                value={dataInicioBloq}
                onChange={(e) =>
                  setBloqueioForm((f) => ({
                    ...f,
                    dataInicio: e.target.value,
                    dataFim:
                      f.preset === 'custom'
                        ? f.dataFim || e.target.value
                        : fimPorPreset(e.target.value, f.preset),
                  }))
                }
              />
            </label>
            <label className="text-xs">
              Data fim
              <input
                type="date"
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                value={dataFimBloq}
                disabled={bloqueioForm.preset !== 'custom'}
                onChange={(e) =>
                  setBloqueioForm((f) => ({ ...f, dataFim: e.target.value, preset: 'custom' }))
                }
              />
            </label>

            <label className="text-xs">
              Início
              <input
                type="time"
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                value={bloqueioForm.horaInicio}
                onChange={(e) =>
                  setBloqueioForm((f) => ({ ...f, horaInicio: e.target.value }))
                }
              />
            </label>
            <label className="text-xs">
              Fim
              <input
                type="time"
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                value={bloqueioForm.horaFim}
                onChange={(e) => setBloqueioForm((f) => ({ ...f, horaFim: e.target.value }))}
              />
            </label>
            <label className="col-span-2 text-xs">
              Motivo
              <input
                className="mt-1 w-full rounded-lg border px-2 py-1.5 text-sm"
                value={bloqueioForm.motivo}
                onChange={(e) => setBloqueioForm((f) => ({ ...f, motivo: e.target.value }))}
              />
            </label>
          </div>
          <p className="mt-2 text-[11px] text-neutral-500">
            Vai bloquear: <strong>{dataInicioBloq}</strong> → <strong>{dataFimBloq}</strong> (
            {listarDatasEntre(dataInicioBloq, dataFimBloq).length} dia
            {listarDatasEntre(dataInicioBloq, dataFimBloq).length === 1 ? '' : 's'}) ·{' '}
            {bloqueioForm.horaInicio}–{bloqueioForm.horaFim}
          </p>
          <button type="button" onClick={adicionarBloqueio} className="bb-btn-dark mt-3">
            <Ban className="h-3.5 w-3.5" /> Bloquear
          </button>
          {msgBloqueio && (
            <p className="mt-2 text-xs font-medium text-emerald-800">{msgBloqueio}</p>
          )}
          <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-xs text-neutral-700">
            <li className="font-semibold text-neutral-500">Bloqueios em {dataRef}:</li>
            {bloqueiosDoDia.length === 0 && (
              <li className="text-neutral-400">Nenhum neste dia.</li>
            )}
            {bloqueiosDoDia.map((b) => {
              const nome =
                b.barbeiroId == null
                  ? 'Todos'
                  : estado.barbeiros.find((x) => x.id === b.barbeiroId)?.nome || '?';
              return (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 px-2 py-1"
                >
                  <span>
                    {b.horaInicio}–{b.horaFim} · {nome} · {b.motivo}
                  </span>
                  <button
                    type="button"
                    className="text-red-600"
                    onClick={() => removerBloqueio(b.id)}
                  >
                    remover
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="bb-card" data-tour="adm-comissao">
          <h3 className="mb-2 text-sm font-bold uppercase text-neutral-800">
            Comissão por barbeiro
          </h3>
          <ul className="space-y-2">
            {estado.barbeiros.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="font-semibold">{b.nome}</span>
                <label className="flex items-center gap-1 text-xs text-neutral-600">
                  %
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="w-16 rounded border px-2 py-1 text-sm"
                    value={b.comissaoPct}
                    onChange={(e) => setComissao(b.id, Number(e.target.value))}
                  />
                </label>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
